/**
 * Journal ⇄ chat tab messages (boona repo: docs/codebase/inbox/README.md §7.7).
 *
 * Both apps live on admin.boona.app, so they share an origin and can talk directly:
 * BroadcastChannel where the browser has it, otherwise a localStorage key plus the
 * `storage` event (Safari 14 — BroadcastChannel arrived in 15.4). The storage event
 * fires in every OTHER tab of the origin, which is exactly the audience.
 *
 * A request carries an id; the tab that acts on it answers with an `ack` of that id,
 * so the sender knows whether anybody listened and can open the tab itself if not.
 * When the sender has to open the journal, the request travels in a one-shot
 * mailbox (PENDING_KEY) the journal reads on boot — never in the URL, which would
 * put a client's name in the address bar and the browser history.
 */

export const CHANNEL_NAME = "boona";
export const STORAGE_KEY = "boona:xtab";
export const PENDING_KEY = "boona:xtab-pending";
export const ACK_TIMEOUT_MS = 1000;
/** A mailbox entry older than this was left by a tab that never opened. */
export const PENDING_TTL_MS = 60_000;

export type BookingClient = { id: number; name: string; phone?: string | null };

export type CrossTabRequest =
  | { type: "open-appointment"; id: string; recordId: number }
  | { type: "new-booking"; id: string; client: BookingClient };

export type CrossTabAck = { type: "ack"; id: string; of: string; by: "journal" | "inbox" };

export type CrossTabMessage = CrossTabRequest | CrossTabAck;

export interface CrossTabTransport {
  post(msg: CrossTabMessage): void;
  subscribe(fn: (msg: CrossTabMessage) => void): () => void;
  close(): void;
}

export function isCrossTabMessage(x: unknown): x is CrossTabMessage {
  if (!x || typeof x !== "object") return false;
  const m = x as Record<string, unknown>;
  if (typeof m.id !== "string") return false;
  switch (m.type) {
    case "open-appointment":
      return typeof m.recordId === "number";
    case "new-booking": {
      const c = m.client as Record<string, unknown> | undefined;
      return !!c && typeof c.id === "number" && typeof c.name === "string";
    }
    case "ack":
      return typeof m.of === "string" && (m.by === "journal" || m.by === "inbox");
    default:
      return false;
  }
}

type Listener = (msg: CrossTabMessage) => void;

interface BroadcastChannelLike {
  onmessage: ((ev: { data: unknown }) => void) | null;
  postMessage(data: unknown): void;
  close(): void;
}

export function broadcastTransport(
  Ctor: new (name: string) => BroadcastChannelLike = BroadcastChannel as unknown as new (name: string) => BroadcastChannelLike,
): CrossTabTransport {
  const channel = new Ctor(CHANNEL_NAME);
  const listeners = new Set<Listener>();
  channel.onmessage = (ev) => {
    if (isCrossTabMessage(ev.data)) listeners.forEach((fn) => fn(ev.data as CrossTabMessage));
  };
  return {
    post: (msg) => channel.postMessage(msg),
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    close: () => {
      listeners.clear();
      channel.close();
    },
  };
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

type StorageEventLike = { key: string | null; newValue: string | null };

export interface StorageEventTarget {
  addEventListener(type: "storage", fn: (e: StorageEventLike) => void): void;
  removeEventListener(type: "storage", fn: (e: StorageEventLike) => void): void;
}

export function storageTransport(
  storage: StorageLike = window.localStorage,
  target: StorageEventTarget = window as unknown as StorageEventTarget,
): CrossTabTransport {
  const listeners = new Set<Listener>();
  const onStorage = (e: StorageEventLike) => {
    if (e.key !== STORAGE_KEY || !e.newValue) return;
    try {
      const { msg } = JSON.parse(e.newValue);
      if (isCrossTabMessage(msg)) listeners.forEach((fn) => fn(msg));
    } catch {
      // somebody else's value under our key — ignore
    }
  };
  target.addEventListener("storage", onStorage);
  return {
    post(msg) {
      try {
        // the nonce makes two identical messages in a row still a change; removing the
        // key at once keeps storage clean (the removal event carries no value)
        storage.setItem(STORAGE_KEY, JSON.stringify({ msg, nonce: Math.random().toString(36).slice(2) }));
        storage.removeItem(STORAGE_KEY);
      } catch {
        // storage unavailable (private mode) — the ack timeout covers it
      }
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    close() {
      listeners.clear();
      target.removeEventListener("storage", onStorage);
    },
  };
}

export function createCrossTab(): CrossTabTransport {
  return typeof BroadcastChannel === "function" ? broadcastTransport() : storageTransport();
}

let seq = 0;
export function newMessageId(): string {
  seq += 1;
  return `${Date.now().toString(36)}-${seq.toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

/** Post a request and wait for its ack; false when no tab answered in time. */
export function request(
  transport: CrossTabTransport,
  msg: CrossTabRequest,
  timeoutMs: number = ACK_TIMEOUT_MS,
): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (ok: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      unsubscribe();
      resolve(ok);
    };
    const unsubscribe = transport.subscribe((m) => {
      if (m.type === "ack" && m.of === msg.id) finish(true);
    });
    const timer = setTimeout(() => finish(false), timeoutMs);
    transport.post(msg);
  });
}

export type RequestHandlers = {
  [K in CrossTabRequest["type"]]?: (msg: Extract<CrossTabRequest, { type: K }>) => boolean;
};

/** Act on requests; a handler returns true when it acted, and only then is an ack sent. */
export function serve(
  transport: CrossTabTransport,
  by: CrossTabAck["by"],
  handlers: RequestHandlers,
): () => void {
  return transport.subscribe((msg) => {
    if (msg.type === "ack") return;
    const handler = handlers[msg.type] as ((m: CrossTabRequest) => boolean) | undefined;
    if (handler && handler(msg)) transport.post({ type: "ack", id: newMessageId(), of: msg.id, by });
  });
}

/** Leave a request for a tab that is about to be opened (see the header). */
export function leavePending(msg: CrossTabRequest, storage: StorageLike = window.localStorage, now = Date.now()): void {
  try {
    storage.setItem(PENDING_KEY, JSON.stringify({ msg, t: now }));
  } catch {
    // nothing to do: the opened tab just shows the journal
  }
}

/** Take (and clear) a fresh mailbox request, if any. */
export function takePending(storage: StorageLike = window.localStorage, now = Date.now()): CrossTabRequest | null {
  try {
    const raw = storage.getItem(PENDING_KEY);
    if (!raw) return null;
    storage.removeItem(PENDING_KEY);
    const { msg, t } = JSON.parse(raw);
    if (typeof t !== "number" || now - t > PENDING_TTL_MS) return null;
    return isCrossTabMessage(msg) && msg.type !== "ack" ? msg : null;
  } catch {
    return null;
  }
}
